import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { Carousel } from "react-responsive-carousel";
import { PiCaretLeft, PiCaretRight } from "react-icons/pi";
import {
	WindowsPlatformIcon,
	ApplePlatformIcon,
} from "./ransomware-file-icons";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "./preview-image-modal.css";

const hasImageSrc = (src) => typeof src === "string" && src.trim().length > 0;

const buildPreviewSections = (windowsImageSrc, macImageSrc) =>
	[
		hasImageSrc(windowsImageSrc) && {
			label: "Windows",
			platform: "windows",
			src: windowsImageSrc,
		},
		hasImageSrc(macImageSrc) && {
			label: "Mac",
			platform: "mac",
			src: macImageSrc,
		},
	].filter(Boolean);

const PreviewHeaderPlatformIcon = ({ platform }) => {
	if (platform === "mac") {
		return <ApplePlatformIcon />;
	}
	if (platform === "windows") {
		return <WindowsPlatformIcon />;
	}
	return null;
};

export default function PreviewImageModal({
	show,
	onHide,
	windowsImageSrc,
	macImageSrc,
	fileName,
}) {
	const sections = useMemo(
		() => buildPreviewSections(windowsImageSrc, macImageSrc),
		[windowsImageSrc, macImageSrc]
	);
	const [activeIndex, setActiveIndex] = useState(0);
	const slideCount = sections.length;

	useEffect(() => {
		if (show) {
			setActiveIndex(0);
		}
	}, [show, windowsImageSrc, macImageSrc]);

	const goToPrev = () => {
		setActiveIndex((index) =>
			index <= 0 ? slideCount - 1 : index - 1
		);
	};

	const goToNext = () => {
		setActiveIndex((index) =>
			index >= slideCount - 1 ? 0 : index + 1
		);
	};

	const displayName =
		fileName && fileName.trim().length > 0
			? fileName.trim()
			: "Ransomware file";

	const activePlatform =
		sections.length > 0
			? sections[activeIndex]?.platform || sections[0].platform
			: null;

	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			size="lg"
			className="preview-image-modal"
			contentClassName="preview-image-modal__content"
		>
			<Modal.Header closeButton className="preview-image-modal__header">
				<div className="preview-image-modal__header-inner">
					{activePlatform && (
						<span
							className={
								"preview-image-modal__header-icon preview-image-modal__header-icon--" +
								activePlatform
							}
							aria-hidden="true"
						>
							<PreviewHeaderPlatformIcon platform={activePlatform} />
						</span>
					)}
					<div className="preview-image-modal__header-text">
						<Modal.Title className="preview-image-modal__title">
							{displayName}
						</Modal.Title>
					</div>
				</div>
			</Modal.Header>
			<Modal.Body className="preview-image-modal__body">
				{sections.length === 0 ? (
					<p className="preview-image-modal__empty">
						No screen image uploaded for this ransomware file.
					</p>
				) : sections.length === 1 ? (
					<div className="preview-image-modal__viewer">
						<div className="preview-image-modal__stage">
							<img
								src={sections[0].src}
								alt={`${sections[0].label} ransomware preview`}
								className="preview-image-modal__image"
							/>
						</div>
					</div>
				) : (
					<div className="preview-image-modal__viewer preview-image-modal__viewer--slider">
						<div className="preview-image-modal__stage-wrap">
							<button
								type="button"
								className="preview-image-modal__arrow preview-image-modal__arrow--prev"
								onClick={goToPrev}
								aria-label="Previous screen"
							>
								<PiCaretLeft />
							</button>

							<Carousel
								className="preview-image-modal__carousel"
								selectedItem={activeIndex}
								onChange={setActiveIndex}
								showThumbs={false}
								showIndicators={false}
								showStatus={false}
								infiniteLoop
								useKeyboardArrows
								renderArrowPrev={() => null}
								renderArrowNext={() => null}
							>
								{sections.map(({ label, src }) => (
									<div key={label} className="preview-image-modal__slide">
										<div className="preview-image-modal__stage">
											<img
												src={src}
												alt={`${label} ransomware preview`}
												className="preview-image-modal__image"
											/>
										</div>
									</div>
								))}
							</Carousel>

							<button
								type="button"
								className="preview-image-modal__arrow preview-image-modal__arrow--next"
								onClick={goToNext}
								aria-label="Next screen"
							>
								<PiCaretRight />
							</button>
						</div>

						<div
							className="preview-image-modal__indicators"
							role="tablist"
							aria-label="Screen previews"
						>
							{sections.map(({ label, src }, index) => {
								const isSelected = activeIndex === index;
								return (
									<button
										key={label}
										type="button"
										role="tab"
										aria-selected={isSelected}
										aria-label={`${label} screen`}
										title={label}
										className={
											"preview-image-modal__indicator" +
											(isSelected
												? " preview-image-modal__indicator--active"
												: "")
										}
										onClick={() => setActiveIndex(index)}
									>
										<img
											src={src}
											alt=""
											className="preview-image-modal__indicator-img"
										/>
										<span className="preview-image-modal__indicator-label">
											{label}
										</span>
									</button>
								);
							})}
						</div>
					</div>
				)}
			</Modal.Body>
		</Modal>
	);
}
